/**
 * import react
 */
import React, {useCallback, useRef, useState} from 'react';

/**
 * import material-ui
 */
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

/**
 * import components
 */
import CameraTable from './camera-table';
import SlotTable from './slot-table';
import AuthDialog from './auth-dialog';
import MessageDialog from './msg-dialog';

/**
 * import app-actions
 */
import AppActions from '../../../actions/app-actions';

/**
 * import UI helper
 */
// import { confDetailsJsonToObj } from './helper';
import {getSlotUILogic, mapTestedData} from './helper';
import {constVal} from '../slot-const';

const SlotAllocation = (props) => {
  const [cameraData, setCameraData] = useState(props.mapCameraSlotDetails.mapCameras || { list: []});
  const [slotData, setSlotData] = useState(props.mapCameraSlotDetails.mapSlotConfig || { slots: []});
  const [cameraSelectedIdx, setCameraSelectedIdx] = useState(constVal.INVALID_INDEX);
  const [slotSelectedIdx, setSlotSelectedIdx] = useState(constVal.INVALID_INDEX);
  const [auth, setAuth] = useState({ open: false });
  const [msgDialog, setMsgDialog] = useState({ open: false });
  const [actionDisabled, setActionDisabled] = useState(true);
  const lconfRef = useRef(null);

  const handleCameraListRowSelected = useCallback((idx) => {
    const invalidIdx = constVal.INVALID_INDEX;
    const selectedIdx = cameraSelectedIdx !== idx ? idx : invalidIdx;
    setCameraSelectedIdx(() => selectedIdx);
    if (selectedIdx !== invalidIdx && slotSelectedIdx !== invalidIdx) {
      handleAssignOnClick({ cameraSelectedIdx: selectedIdx, slotSelectedIdx });
    }
  }, [cameraSelectedIdx, slotSelectedIdx]);

  const handleSlotListRowSelected = useCallback((idx) => {
    const invalidIdx = constVal.INVALID_INDEX;
    const selectedIdx = slotSelectedIdx !== idx ? idx : invalidIdx;
    setSlotSelectedIdx(() => selectedIdx);
    if (cameraSelectedIdx !== invalidIdx && selectedIdx !== invalidIdx) {
      handleAssignOnClick({ cameraSelectedIdx, slotSelectedIdx: selectedIdx });
    }
  }, [cameraSelectedIdx, slotSelectedIdx]);

  const handleUpdateComment = useCallback((idx, comment) => {
    setSlotData(prev => {
      const slots = [...prev.slots.slice(0, idx),
        { ...prev.slots[idx], ui: { ...prev.slots[idx].ui, comment }, cameraComment: comment },
        ...prev.slots.slice(idx + 1)];
      return { ...prev, slots };
    });
    setActionDisabled(() => false);
  }, []);

  const handleTestedSlot = useCallback(({ data = {}, slotIdx }) => {
    setSlotData(prev => {
      const { macAddr, ipAddr } = prev.slots[slotIdx].ui;
      const testedData = mapTestedData(data, macAddr, ipAddr);
      const slots = [...prev.slots.slice(0, slotIdx),
        {
          ...prev.slots[slotIdx],
          ui: {
            ...prev.slots[slotIdx].ui,
            testedStatus: data.testedStatus,
            comment: data.comment
          },
          ...testedData
        },
        ...prev.slots.slice(slotIdx + 1)];
      return { ...prev, slots };
    });
    setActionDisabled(() => false);
  }, []);

  const handleRtspPreferred = useCallback(([nvrRecRtsp = '', aiRtsp = ''], slotIdx) => {
    const slots = [...slotData.slots.slice(0, slotIdx),
      {
        ...slotData.slots[slotIdx],
        cameraNvrRecRtspPreferred: nvrRecRtsp || slotData.slots[slotIdx].cameraNvrRecRtspPreferred,
        cameraAnalyzeRtspPreferred: aiRtsp || slotData.slots[slotIdx].cameraAnalyzeRtspPreferred
      },
      ...slotData.slots.slice(slotIdx + 1)];
    setSlotData((prev) => ({ ...prev, slots }));
    setActionDisabled(prev => ({ ...prev, save: false }));
  }, [slotData]);

  const handleAssignOnClick = ({ cameraSelectedIdx, slotSelectedIdx }) => {
    const camera = cameraData.list[cameraSelectedIdx] || {};
    const handleAuthDialogClose = (data) => {
      const { macAddr, ipAddr } = camera;
      const authData = mapTestedData(data, macAddr, ipAddr);
      setCameraData(prev => {
        let { assignedCount } = prev.list[cameraSelectedIdx].ui;
        assignedCount += 1;
        const list = [...prev.list.slice(0, cameraSelectedIdx),
          { ...prev.list[cameraSelectedIdx], ui: { ...prev.list[cameraSelectedIdx].ui, isAssigned: true, assignedCount } },
          ...prev.list.slice(cameraSelectedIdx + 1)];
        return { ...prev, list };
      });
      setSlotData(prev => {
        const slots = [...prev.slots.slice(0, slotSelectedIdx),
          {
            ...prev.slots[slotSelectedIdx],
            ui: {
              ...getSlotUILogic(false),
              ipAddr: camera.ipAddr,
              port: camera.port,
              macAddr: camera.macAddr,
              cameraListIdx: cameraSelectedIdx,
              comment: data.comment
            },
            ...authData
          },
          ...prev.slots.slice(slotSelectedIdx + 1)];

        return { ...prev, slots };
      });
      setCameraSelectedIdx(constVal.INVALID_INDEX);
      setSlotSelectedIdx(constVal.INVALID_INDEX);
      setActionDisabled(() => false);
      setAuth(() => ({ open: false }));
    };

    if (cameraSelectedIdx !== constVal.INVALID_INDEX && slotSelectedIdx !== constVal.INVALID_INDEX) {
      if (slotData.slots[slotSelectedIdx].cameraMAC) {
        setMsgDialog(() => ({
          open: true,
          type: constVal.MSG_TYPE_ERROR,
          title: 'Error',
          content: 'Please remove the already assigned camera in the selected slot and then try',
          handleCancelOnClick: () => { setMsgDialog(() => ({ open: false })); }
        }));
      } else {
        const { ipAddr = '', port = 0 } = camera || {};
        const helpText = `Assigning camera ${ipAddr} - ${port} to slot ${slotSelectedIdx + 1}`;
        setAuth(() => ({ open: true, helpText, handleAuthDialogClose, isAssign: true }));
      }
    } else {
      setMsgDialog(() => ({
        open: true,
        type: constVal.MSG_TYPE_ERROR,
        title: 'Error',
        content: 'Please choose a camera and a slot',
        handleCancelOnClick: () => { setMsgDialog(() => ({ open: false })); }
      }));
    }
  };

  const handleRemoveOnClick = () => {
    let type = constVal.MSG_TYPE_ERROR;
    let title = 'Error';
    let content = 'Please choose the slot';

    const handleOkOnClick = () => {
      const cameraIdx = slotData.slots[slotSelectedIdx].ui.cameraListIdx;
      if (cameraIdx !== constVal.INVALID_INDEX) {
        setCameraData(prev => {
          let { assignedCount } = prev.list[cameraIdx].ui;
          assignedCount -= 1;
          const list = [...prev.list.slice(0, cameraIdx),
            { ...prev.list[cameraIdx], ui: { ...prev.list[cameraIdx].ui, isAssigned: assignedCount > 0, assignedCount } },
            ...prev.list.slice(cameraIdx + 1)];
          return { ...prev, list };
        });
      }
      setSlotData(prev => {
        const slots = [...prev.slots.slice(0, slotSelectedIdx),
          { ui: getSlotUILogic(false) },
          ...prev.slots.slice(slotSelectedIdx + 1)];

        return { ...prev, slots };
      });
      setSlotSelectedIdx(constVal.INVALID_INDEX);
      setActionDisabled(() => false);
      setMsgDialog(() => ({ open: false }));
    };

    const handleCancelOnClick = () => setMsgDialog(() => ({ open: false }));

    if (slotSelectedIdx !== constVal.INVALID_INDEX) {
      const { slots = [] } = slotData || {};
      if (slots[slotSelectedIdx] && slots[slotSelectedIdx].ui.macAddr !== '') {
        const { ipAddr = '', port = 0, macAddr } = slots[slotSelectedIdx].ui;
        const camera = ipAddr ? `${ipAddr} - ${port}` : macAddr;
        type = constVal.MSG_TYPE_CONFIRMATION;
        title = 'Confirmation';
        content = `Do you want to remove the associated camera ${camera} from slot ${slotSelectedIdx + 1}`;
      } else {
        content = `Selected slot doesn't associated to any camera`;
      }
    }
    setMsgDialog(() => ({ open: true, type, title, content, handleOkOnClick, handleCancelOnClick }));
  };

  const removeUIDetails = () => {
    const { slots = [] } = slotData;
    return slots.map((slot) => {
      // eslint-disable-next-line no-unused-vars
      const { ui = {}, ...rest } = slot;
      return {...rest};
    });
  }

  const handleVerifyComplete = (complete) => {
    const slotDetails = removeUIDetails();
    const { confDetailsObj, localConfInfo} = props;
    const basic_installation = {...confDetailsObj.basic_installation, install_progress_step: complete ? 5: 3};
    const slot_configuration = {...confDetailsObj.slot_configuration, slots: slotDetails};
    const config_details = JSON.stringify({...confDetailsObj, slot_configuration, basic_installation});
    lconfRef.current = config_details;
    setMsgDialog(() => ({
      open: true,
      type: constVal.MSG_TYPE_PROGRESS,
      title: 'Info',
      content: complete ? 'Please wait sending complete request in-progress' : 'Please wait sending verify request in-pgoress'
    }));
    AppActions.patchLocalConfigById(localConfInfo.device_id, JSON.stringify({config_details}))
      .then(() => {
        setMsgDialog(() => ({
          open: true,
          type: constVal.MSG_TYPE_INFORMATION,
          content: complete ? 'Basic installation complete successfully' : 'Verification request send successfully',
          handleCancelOnClick: () => {
            setMsgDialog(() => ({ open: false}));
            props.handleSetHasBeenDialogProgress(false);
            props.updateBasicInstall({...localConfInfo, config_details});
          }
        }));
      })
      .catch(() => {
        setMsgDialog(() => ({
          open: true,
          content: complete ? 'Complete request failed' : 'Verification request failed',
          handleCancelOnClick: () => {
            setMsgDialog(() => ({open: false}));
            props.handleSetHasBeenDialogProgress(false);
          }
        }));
      })
  };

  const getCameraAndSlotList = () => {
    setCamerasUuidInSlot(slotData.slots, props.confDetailsObj.basic_installation)
    if (slotData.nb_max_slot > 0) {
      return (
        <Grid container spacing={4}>
          <Grid item>
            {
              cameraData.list.length === 0
                ? (
                  <>
                    <Typography variant="h6" style={{ paddingLeft: 8 }}>Camera List</Typography>
                    <Typography variant='h6' align='center' style={{ paddingTop: 16 }}>No camera found on the network</Typography>
                  </>
                )
                : (
                <>
                  <Typography variant="h6" style={{ paddingLeft: 8 }}>Camera List</Typography>
                  <CameraTable list={cameraData.list} selectedIdx={cameraSelectedIdx}
                    handleCameraListRowSelected={handleCameraListRowSelected} />
                </>
                )
            }
          </Grid>
          <Grid item>
            <Typography variant="h6" style={{ paddingLeft: 8 }}>Slot List</Typography>
            <SlotTable list={slotData.slots} selectedIdx={slotSelectedIdx}
              handleTestedSlot={handleTestedSlot}
              handleSlotListRowSelected={handleSlotListRowSelected}
              handleUpdateComment={handleUpdateComment}
              handleRtspPreferred={handleRtspPreferred} />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <div style={{ textAlign: 'center' }}>
          <Typography variant='h6'>Slots are not properly configured</Typography>
        </div>
      );
    }
  };

  function setCamerasUuidInSlot(slots, installation) {
    slots?.forEach(detail => {
      const detailMac = detail.cameraMAC;
      const filteredByMacCameras = installation?.camera_discovered?.camerasTable?.filter(camera => camera.macAddr === detailMac);
      if(filteredByMacCameras !== undefined && filteredByMacCameras.length > 0) {
        const cameraMetadata = filteredByMacCameras[0]?.additionalMetadata;
        if(cameraMetadata !== undefined) {
          const lastColonIndex = cameraMetadata.lastIndexOf(':');
          detail.cameraUUID = cameraMetadata.substring(lastColonIndex + 1, cameraMetadata.length);
        }
      }
    });
  }

  const getActionsList = () => {
    if (slotData.nb_max_slot > 0) {
      const {basic_installation={}} = props.confDetailsObj || {};
      return (
        <>
          <Button color='primary' onClick={handleRemoveOnClick}>Remove</Button>
          <Button color='primary' disabled={actionDisabled} onClick={() => handleVerifyComplete(false)}>Verify</Button>
          <Button color='primary'
            disabled={!(basic_installation.install_progress_step > constVal.BASIC_INSTALL_VERIFY)}
            onClick={() => handleVerifyComplete(true)}
          >
            Complete
          </Button>
          <Button
            color='primary'
            onClick={() => {
              props.handleDlgState();
              props.handleSetHasBeenDialogProgress(false);
            }}
          >
            Close
          </Button>
        </>
      );
    } else {
      return (
        <Button
          color='primary'
          autoFocus
          onClick={() => {
            props.handleDlgState();
            props.handleSetHasBeenDialogProgress(false);
          }}
        >
          Close
        </Button>
      );
    }
  };

  return (
    <>
      <Dialog open={true} maxWidth='lg'>
        <DialogTitle>Slot Allocation</DialogTitle>
        <DialogContent style={{ backgroundColor: 'lightgray' }}>
          {getCameraAndSlotList()}
        </DialogContent>
        <DialogActions>
          {getActionsList()}
        </DialogActions>
      </Dialog>
      {msgDialog.open ? <MessageDialog {...msgDialog} /> : null}
      {auth.open ? <AuthDialog {...auth}/> : null}
    </>
  );
};

export default React.memo(SlotAllocation);
